import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Package, User, ArrowUpRight, BadgeCheck } from 'lucide-react';
import { getApps } from '@/lib/api';

export default function DevelopersPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: () => getApps(),
  });

  const uniqueAuthors = Array.from(
    new Set(
      apps.map(app => app.developer_pubkey).filter(a => a && a !== 'Unknown')
    )
  );

  const developers = uniqueAuthors.map(author => {
    const developerApps = apps.filter(app => app.developer_pubkey === author);
    return {
      pubkey: author,
      verified: developerApps.some(app => app.verified),
      appCount: developerApps.length,
      latestApp: developerApps[0]?.name || 'Unknown',
    };
  });

  const filteredDevelopers = developers.filter(dev => {
    const q = searchTerm.toLowerCase();
    return (
      dev.pubkey.toLowerCase().includes(q) ||
      dev.latestApp.toLowerCase().includes(q)
    );
  });

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-5 bg-white/[0.06] rounded w-1/4'></div>
          <div className='h-3.5 bg-white/[0.06] rounded w-1/3'></div>
          <div className='space-y-3 mt-6'>
            {[1, 2, 3].map(i => (
              <div key={i} className='h-16 bg-white/[0.04] rounded-lg'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='animate-fade-in'>
        <h1 className='text-xl font-semibold text-neutral-100'>Developers</h1>
        <p className='mt-1 text-[13px] text-neutral-500 font-light'>
          Browse developers and their published applications
        </p>
      </div>

      {/* Search */}
      <div className='relative max-w-sm animate-slide-up stagger-1'>
        <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 h-3.5 w-3.5' />
        <input
          type='text'
          placeholder='Search developers or apps...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className='input pl-9'
        />
      </div>

      {/* List */}
      {filteredDevelopers.length === 0 ? (
        <div className='text-center py-16'>
          <User className='mx-auto h-8 w-8 text-neutral-600' />
          <p className='mt-3 text-[13px] text-neutral-400'>
            {searchTerm
              ? 'No developers match your search.'
              : 'No developers have published applications yet.'}
          </p>
        </div>
      ) : (
        <div className='space-y-2 animate-slide-up stagger-2'>
          {filteredDevelopers.map(developer => (
            <DeveloperCard key={developer.pubkey} developer={developer} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeveloperCard({
  developer,
}: {
  developer: {
    pubkey: string;
    verified: boolean;
    appCount: number;
    latestApp: string;
  };
}) {
  const displayName = developer.pubkey;
  return (
    <Link
      to={`/developers/${encodeURIComponent(developer.pubkey)}`}
      className='card block px-4 py-3 group hover:border-brand-600/30 glow-border'
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3 min-w-0'>
          <div className='flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.06] border border-white/[0.06] flex-shrink-0'>
            <User className='w-3.5 h-3.5 text-neutral-400' />
          </div>
          <div className='min-w-0'>
            <h3 className='flex items-center gap-1.5 text-[13px] font-medium text-neutral-200 truncate group-hover:text-white transition-colors'>
              <span className='font-mono truncate'>{displayName}</span>
              {developer.verified && (
                <BadgeCheck className='h-3.5 w-3.5 flex-shrink-0 text-emerald-400' />
              )}
            </h3>
            <p className='text-[11px] text-neutral-500 font-light'>
              Latest: {developer.latestApp}
            </p>
          </div>
        </div>
        <div className='flex items-center gap-3 flex-shrink-0 ml-4'>
          <div className='flex items-center gap-1 text-[11px] text-neutral-500'>
            <Package className='w-3 h-3' />
            {developer.appCount}
          </div>
          <ArrowUpRight className='w-3.5 h-3.5 text-neutral-600 group-hover:text-brand-600 transition-all' />
        </div>
      </div>
    </Link>
  );
}
